import { Plus, Trash2 } from "lucide-react";
import { ui } from "@patient-watch/ui";
import { MarkdownFormatHint, RichLabel } from "@patient-watch/forms";

import {
    ALLOWED_FORM_DRAFT_INPUT_KEYS,
    DEFAULT_SCALE_OPTIONS,
    FORM_DRAFT_MAX_INPUTS_PER_PAGE,
    FORM_DRAFT_MAX_PAGES,
    type AllowedFormDraftInputKey,
    type FormDraft,
    type FormDraftInput,
    type FormDraftPage,
} from "./formDraft";

const INPUT_TYPE_LABELS: Record<AllowedFormDraftInputKey, string> = {
    "scale-rating": "Scale rating",
    "single-choice": "Single choice",
    "multiple-choice": "Multiple choice",
    "yes-no": "Yes / No",
    checkbox: "Checkbox",
    dropdown: "Dropdown",
    "short-text": "Short text",
    "long-text": "Long text",
    number: "Number",
    slider: "Slider",
    "date-picker": "Date picker",
};

const CHOICE_INPUT_KEYS = new Set<AllowedFormDraftInputKey>(["single-choice", "multiple-choice", "dropdown"]);

function newQuestion(): FormDraftInput {
    return { input_key: "short-text", input_label: "New question", is_optional: false };
}

function forInputType(input: FormDraftInput, inputKey: AllowedFormDraftInputKey): FormDraftInput {
    const next: FormDraftInput = {
        input_id: input.input_id,
        input_key: inputKey,
        input_label: input.input_label,
        input_description: input.input_description,
        is_optional: input.is_optional,
    };
    if (CHOICE_INPUT_KEYS.has(inputKey))
        next.options = input.options?.length ? input.options : ["Option 1", "Option 2"];
    if (inputKey === "scale-rating") {
        next.scale_options = input.scale_options?.length
            ? input.scale_options
            : DEFAULT_SCALE_OPTIONS.map((option) => ({ ...option }));
    }
    if (inputKey === "number") {
        next.min_value = input.min_value;
        next.max_value = input.max_value;
    }
    if (inputKey === "slider") {
        next.min_value = input.min_value ?? 0;
        next.max_value = input.max_value ?? 10;
    }
    return next;
}

function OptionsEditor({
    prefix,
    options,
    onChange,
}: {
    prefix: string;
    options: string[];
    onChange: (options: string[]) => void;
}) {
    return (
        <div className="space-y-2">
            <p className="text-xs font-medium text-muted-foreground">Options</p>
            {options.map((option, optionIndex) => (
                <div key={optionIndex} className="flex items-center gap-2">
                    <ui.Input
                        aria-label={prefix + " option " + (optionIndex + 1)}
                        value={option}
                        onChange={(event) =>
                            onChange(options.map((item, index) => (index === optionIndex ? event.target.value : item)))
                        }
                    />
                    <ui.Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        aria-label={"Remove " + prefix.toLowerCase() + " option " + (optionIndex + 1)}
                        disabled={options.length === 1}
                        onClick={() => onChange(options.filter((_, index) => index !== optionIndex))}
                    >
                        <Trash2 className="h-4 w-4" />
                    </ui.Button>
                </div>
            ))}
            <ui.Button type="button" variant="outline" size="sm" onClick={() => onChange([...options, "New option"])}>
                <Plus className="h-4 w-4" /> Add option
            </ui.Button>
        </div>
    );
}

function ScaleOptionsEditor({
    prefix,
    options,
    onChange,
}: {
    prefix: string;
    options: Array<{ label: string; value: number }>;
    onChange: (options: Array<{ label: string; value: number }>) => void;
}) {
    return (
        <div className="space-y-2">
            <p className="text-xs font-medium text-muted-foreground">Scale options</p>
            {options.map((option, optionIndex) => (
                <div key={optionIndex} className="grid grid-cols-[1fr_7rem_auto] gap-2">
                    <ui.Input
                        aria-label={prefix + " scale option " + (optionIndex + 1) + " label"}
                        value={option.label}
                        onChange={(event) =>
                            onChange(
                                options.map((item, index) =>
                                    index === optionIndex ? { ...item, label: event.target.value } : item
                                )
                            )
                        }
                    />
                    <ui.Input
                        type="number"
                        aria-label={prefix + " scale option " + (optionIndex + 1) + " value"}
                        value={option.value}
                        onChange={(event) =>
                            onChange(
                                options.map((item, index) =>
                                    index === optionIndex ? { ...item, value: Number(event.target.value) } : item
                                )
                            )
                        }
                    />
                    <ui.Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        aria-label={"Remove " + prefix.toLowerCase() + " scale option " + (optionIndex + 1)}
                        disabled={options.length === 1}
                        onClick={() => onChange(options.filter((_, index) => index !== optionIndex))}
                    >
                        <Trash2 className="h-4 w-4" />
                    </ui.Button>
                </div>
            ))}
            <ui.Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => onChange([...options, { label: "New option", value: options.length }])}
            >
                <Plus className="h-4 w-4" /> Add scale option
            </ui.Button>
        </div>
    );
}

function QuestionEditor({
    input,
    pageIndex,
    inputIndex,
    canRemove,
    onChange,
    onRemove,
}: {
    input: FormDraftInput;
    pageIndex: number;
    inputIndex: number;
    canRemove: boolean;
    onChange: (input: FormDraftInput) => void;
    onRemove: () => void;
}) {
    const prefix = "Question " + (pageIndex + 1) + "." + (inputIndex + 1);
    const idPrefix = "ai-draft-question-" + pageIndex + "-" + inputIndex;
    const needsRange = input.input_key === "number" || input.input_key === "slider";

    return (
        <li className="space-y-3 rounded-md border p-3">
            <div className="flex items-start gap-2">
                <div className="grid flex-1 gap-3 sm:grid-cols-2">
                    <div className="space-y-1.5">
                        <label htmlFor={idPrefix + "-type"} className="text-xs font-medium text-muted-foreground">
                            {prefix} type
                        </label>
                        <select
                            id={idPrefix + "-type"}
                            value={input.input_key}
                            onChange={(event) =>
                                onChange(forInputType(input, event.target.value as AllowedFormDraftInputKey))
                            }
                            className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm"
                        >
                            {ALLOWED_FORM_DRAFT_INPUT_KEYS.map((key) => (
                                <option key={key} value={key}>
                                    {INPUT_TYPE_LABELS[key]}
                                </option>
                            ))}
                        </select>
                    </div>
                    <div className="space-y-1.5 sm:col-span-2">
                        <label htmlFor={idPrefix + "-label"} className="text-xs font-medium text-muted-foreground">
                            {prefix} label
                        </label>
                        <ui.Textarea
                            id={idPrefix + "-label"}
                            value={input.input_label}
                            rows={2}
                            onChange={(event) => onChange({ ...input, input_label: event.target.value })}
                        />
                        <MarkdownFormatHint extra="The box below shows how patients see it." />
                        {input.input_label.trim() ? (
                            <div className="rounded-md border bg-muted/20 p-2 text-sm">
                                <RichLabel>{input.input_label}</RichLabel>
                            </div>
                        ) : null}
                    </div>
                    <div className="space-y-1.5 sm:col-span-2">
                        <label
                            htmlFor={idPrefix + "-description"}
                            className="text-xs font-medium text-muted-foreground"
                        >
                            {prefix} description
                        </label>
                        <ui.Textarea
                            id={idPrefix + "-description"}
                            value={input.input_description ?? ""}
                            rows={2}
                            onChange={(event) =>
                                onChange({ ...input, input_description: event.target.value || undefined })
                            }
                        />
                    </div>
                </div>
                <ui.Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    aria-label={"Remove question " + (pageIndex + 1) + "." + (inputIndex + 1)}
                    disabled={!canRemove}
                    onClick={onRemove}
                >
                    <Trash2 className="h-4 w-4" />
                </ui.Button>
            </div>

            <label className="flex items-center gap-2 text-sm">
                <input
                    type="checkbox"
                    checked={input.is_optional === true}
                    onChange={(event) => onChange({ ...input, is_optional: event.target.checked })}
                />
                Optional question
            </label>

            {CHOICE_INPUT_KEYS.has(input.input_key) ? (
                <OptionsEditor
                    prefix={prefix}
                    options={input.options ?? []}
                    onChange={(options) => onChange({ ...input, options })}
                />
            ) : null}

            {input.input_key === "scale-rating" ? (
                <ScaleOptionsEditor
                    prefix={prefix}
                    options={input.scale_options ?? []}
                    onChange={(scale_options) => onChange({ ...input, scale_options })}
                />
            ) : null}

            {needsRange ? (
                <div className="grid gap-3 sm:grid-cols-2">
                    <div className="space-y-1.5">
                        <label htmlFor={idPrefix + "-minimum"} className="text-xs font-medium text-muted-foreground">
                            {prefix} minimum
                        </label>
                        <ui.Input
                            id={idPrefix + "-minimum"}
                            type="number"
                            value={input.min_value ?? ""}
                            onChange={(event) =>
                                onChange({
                                    ...input,
                                    min_value: event.target.value === "" ? undefined : Number(event.target.value),
                                })
                            }
                        />
                    </div>
                    <div className="space-y-1.5">
                        <label htmlFor={idPrefix + "-maximum"} className="text-xs font-medium text-muted-foreground">
                            {prefix} maximum
                        </label>
                        <ui.Input
                            id={idPrefix + "-maximum"}
                            type="number"
                            value={input.max_value ?? ""}
                            onChange={(event) =>
                                onChange({
                                    ...input,
                                    max_value: event.target.value === "" ? undefined : Number(event.target.value),
                                })
                            }
                        />
                    </div>
                </div>
            ) : null}
        </li>
    );
}

export function FormDraftPreview({ draft, onChange }: { draft: FormDraft; onChange: (draft: FormDraft) => void }) {
    const questionCount = draft.pages.reduce((sum, page) => sum + page.inputs.length, 0);

    const updatePage = (pageIndex: number, updater: (page: FormDraftPage) => FormDraftPage) => {
        onChange({
            ...draft,
            pages: draft.pages.map((page, index) => (index === pageIndex ? updater(page) : page)),
        });
    };

    return (
        <div className="space-y-4 rounded-lg border bg-muted/20 p-4">
            <div className="space-y-3">
                <div className="space-y-1.5">
                    <label htmlFor="ai-draft-form-name" className="text-sm font-medium">
                        Form name
                    </label>
                    <ui.Input
                        id="ai-draft-form-name"
                        value={draft.form_name}
                        maxLength={120}
                        onChange={(event) => onChange({ ...draft, form_name: event.target.value })}
                    />
                </div>
                <div className="space-y-1.5">
                    <label htmlFor="ai-draft-form-description" className="text-sm font-medium">
                        Description
                    </label>
                    <ui.Textarea
                        id="ai-draft-form-description"
                        value={draft.form_description}
                        maxLength={500}
                        rows={2}
                        onChange={(event) => onChange({ ...draft, form_description: event.target.value })}
                    />
                </div>
                <p className="text-xs text-muted-foreground">
                    {draft.pages.length} page{draft.pages.length === 1 ? "" : "s"}, {questionCount} question
                    {questionCount === 1 ? "" : "s"}. Review every field before saving.
                </p>
            </div>

            <ol className="space-y-4">
                {draft.pages.map((page, pageIndex) => (
                    <li key={pageIndex} className="space-y-3 rounded-lg border bg-background p-3">
                        <div className="flex items-end gap-2">
                            <div className="flex-1 space-y-1.5">
                                <label htmlFor={"ai-draft-page-" + pageIndex} className="text-sm font-medium">
                                    Page {pageIndex + 1} title
                                </label>
                                <ui.Input
                                    id={"ai-draft-page-" + pageIndex}
                                    value={page.page_label}
                                    onChange={(event) =>
                                        updatePage(pageIndex, (current) => ({
                                            ...current,
                                            page_label: event.target.value,
                                        }))
                                    }
                                />
                            </div>
                            <ui.Button
                                type="button"
                                variant="ghost"
                                size="icon"
                                aria-label={"Remove page " + (pageIndex + 1)}
                                disabled={page.page_id != null || draft.pages.length === 1}
                                onClick={() =>
                                    onChange({ ...draft, pages: draft.pages.filter((_, index) => index !== pageIndex) })
                                }
                            >
                                <Trash2 className="h-4 w-4" />
                            </ui.Button>
                        </div>
                        <div className="space-y-1.5">
                            <label htmlFor={"ai-draft-page-description-" + pageIndex} className="text-sm font-medium">
                                Page {pageIndex + 1} description
                            </label>
                            <ui.Textarea
                                id={"ai-draft-page-description-" + pageIndex}
                                value={page.page_description ?? ""}
                                maxLength={500}
                                rows={2}
                                onChange={(event) =>
                                    updatePage(pageIndex, (current) => ({
                                        ...current,
                                        page_description: event.target.value,
                                    }))
                                }
                            />
                        </div>

                        <ol className="space-y-3">
                            {page.inputs.map((input, inputIndex) => (
                                <QuestionEditor
                                    key={inputIndex}
                                    input={input}
                                    pageIndex={pageIndex}
                                    inputIndex={inputIndex}
                                    canRemove={input.input_id == null && page.inputs.length > 1}
                                    onChange={(nextInput) =>
                                        updatePage(pageIndex, (current) => ({
                                            ...current,
                                            inputs: current.inputs.map((item, index) =>
                                                index === inputIndex ? nextInput : item
                                            ),
                                        }))
                                    }
                                    onRemove={() =>
                                        updatePage(pageIndex, (current) => ({
                                            ...current,
                                            inputs: current.inputs.filter((_, index) => index !== inputIndex),
                                        }))
                                    }
                                />
                            ))}
                        </ol>

                        <ui.Button
                            type="button"
                            variant="outline"
                            size="sm"
                            aria-label={"Add question to " + page.page_label}
                            disabled={page.inputs.length >= FORM_DRAFT_MAX_INPUTS_PER_PAGE}
                            onClick={() =>
                                updatePage(pageIndex, (current) => ({
                                    ...current,
                                    inputs: [...current.inputs, newQuestion()],
                                }))
                            }
                        >
                            <Plus className="h-4 w-4" /> Add question
                        </ui.Button>
                    </li>
                ))}
            </ol>

            <ui.Button
                type="button"
                variant="outline"
                size="sm"
                disabled={draft.pages.length >= FORM_DRAFT_MAX_PAGES}
                onClick={() =>
                    onChange({
                        ...draft,
                        pages: [
                            ...draft.pages,
                            { page_label: "Page " + (draft.pages.length + 1), inputs: [newQuestion()] },
                        ],
                    })
                }
            >
                <Plus className="h-4 w-4" /> Add page
            </ui.Button>
        </div>
    );
}

export function FormDraftSummary({
    draft,
    title = "Draft ready",
    helper = "Continue editing opens this in Form Builder. Nothing is stored until you save.",
}: {
    draft: FormDraft;
    title?: string;
    helper?: string;
}) {
    const questionCount = draft.pages.reduce((sum, page) => sum + page.inputs.length, 0);

    return (
        <div className="space-y-3 rounded-lg border bg-muted/20 p-4" data-testid="form-draft-summary">
            <div className="space-y-1">
                <p className="text-sm font-medium">{title}</p>
                <p className="text-2xs text-muted-foreground">{helper}</p>
            </div>
            <div className="space-y-1">
                <p className="text-base font-medium">
                    <RichLabel>{draft.form_name}</RichLabel>
                </p>
                {draft.form_description ? (
                    <p className="text-sm text-muted-foreground">
                        <RichLabel>{draft.form_description}</RichLabel>
                    </p>
                ) : null}
                <p className="text-sm text-muted-foreground">
                    {draft.pages.length} page{draft.pages.length === 1 ? "" : "s"}, {questionCount} question
                    {questionCount === 1 ? "" : "s"}
                </p>
            </div>
            <ol className="space-y-1 text-sm">
                {draft.pages.map((page, index) => {
                    const firstQuestion = page.inputs[0]?.input_label;
                    const extra = page.inputs.length - 1;
                    return (
                        <li key={page.page_id ?? `${page.page_label}-${index}`}>
                            <span className="font-medium">
                                <RichLabel>{page.page_label}</RichLabel>
                            </span>
                            {firstQuestion ? (
                                <>
                                    {" - "}
                                    <RichLabel>{firstQuestion}</RichLabel>
                                </>
                            ) : null}
                            {extra > 0 ? ` (+${extra} more)` : ""}
                        </li>
                    );
                })}
            </ol>
        </div>
    );
}
